import axios from "../axios";
import { useCallback, useContext, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppContext from "../context/AppContext";
import { LOGIN, SET_ACCOUNTS } from "../store/types";
import { Link, useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import styles from "./Login.module.scss";
import { setRoleName } from "../store/reducers/permission.slice";
import { LoadingButton } from "@mui/lab";
import { Container, Stack } from "@mui/system";
import {
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
} from "@mui/material";
import TempLogo from "../assets/svgs/temp-logo.svg";
import { Form, FormikProvider, useFormik } from "formik";
import { LoginSchema } from "../validations/LoginSchema";
import { authLogin } from "../services/user.service";
import authConfig from "../auth_config";
import useTutorialTrigger from "../hooks/useTutorialTrigger";
import { TUTORIAL_FEATURES } from "../config/tutorials.config";
import { logErrorToSentry } from "../utilis/sentry-logger";
import { redirectAuth0, handleAuthToken, handleTenantFetchByEmail } from "../services/login.service";
import ReCAPTCHA from "react-google-recaptcha";

const Login = (props) => {
  // const { jwt } = props;
  // const isMobile = useIsMobileDevice();

  const [showPassword, setShowPassword] = useState(false);
  const { setJwt } = useContext(AppContext);
  const dispatch = useDispatch();
  const [loginRedirectProcessed, setLoginRedirectProcessed] = useState(false);
  const { isLoginLoading } = useSelector((state) => state.common);
  const handleShowPassword = useCallback(() => {
    setShowPassword((show) => !show);
  }, []);
  const [buttonAuth0Text, setButtonAuth0Text] = useState("Log in to SNF Data");

  const isAuth0Enabled =
    process.env.REACT_APP_AUTH0_ENABLED === "true" &&
    process.env.REACT_APP_AUTH0_DOMAIN &&
    process.env.REACT_APP_AUTH0_CLIENT_ID;

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const navigate = useNavigate();

  const isEmailRequired = process.env.REACT_APP_REQUIRE_EMAIL;

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const [tenants, setTenants] = useState([]);
  const [showTenantSelection, setShowTenantSelection] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { triggerTutorial, trackFeatureUsage } = useTutorialTrigger();
  const dashboardTutorialTriggeredRef = useRef(false);
  const [showRecaptcha, setShowRecaptcha] = useState(false);
  const [enableRecaptcha, setEnableRecaptcha] = useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [requiresCaptcha, setRequiresCaptcha] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const triggerDashboardTutorial = useCallback(async () => {
    trackFeatureUsage(TUTORIAL_FEATURES.DASHBOARD_INTRODUCTION).catch((error) => {
      logErrorToSentry(error, {
        service: 'login',
        event: 'track_dashboard_introduction_error',
        extra: {
          tutorialFeature: TUTORIAL_FEATURES.DASHBOARD_INTRODUCTION
        }
      });
    });
    if (dashboardTutorialTriggeredRef.current) return;
    dashboardTutorialTriggeredRef.current = true;
    try {
      await triggerTutorial(TUTORIAL_FEATURES.DASHBOARD_INTRODUCTION);
    } catch (error) {
      logErrorToSentry(error, {
        service: 'login',
        event: 'trigger_dashboard_tutorial_error',
        extra: {
          tutorialFeature: TUTORIAL_FEATURES.DASHBOARD_INTRODUCTION
        }
      });
    }
  }, [trackFeatureUsage, triggerTutorial]);

  const onRecaptchaResolved = async (token) => {
    if (!showTenantSelection) {
      await loginWithRedirect(token);
    } else {
      await handleTenantRedirect(selectedTenant, token);
    }
  };

  const resetRecaptcha = () => {
    setShowRecaptcha(false);
    setButtonAuth0Text("Log in to SNF Data");
    const errorMessage = "reCAPTCHA failed. Please try again.";
    setEmailError(errorMessage);
    logErrorToSentry(errorMessage, {
      service: "login",
      event: "recaptcha_error",
      extra: {
        email,
        reason: "Expired or failed reCAPTCHA",
        url: window.location.href
      }
    });
  };

  const handleTenantRedirect = useCallback(
    async (tenant, recaptchaToken = null) => {
      if (!tenant || redirecting) return;

      setRedirecting(true);

      let redirectUri = tenant.apiBaseUrl;

      if (isEmailRequired) {
        // const stateRes = await fetchAuth0State(tenant?.id);
        // const state = encodeURIComponent(stateRes);
        if (tenant.id) {
          redirectUri = `${tenant.apiBaseUrl}/api/user/auth0/callback`; // if need ?state=${state} add with recaptchaToken 
        } else {
          redirectUri = `${tenant.apiBaseUrl}`; // if need ?state=${state} add with recaptchaToken 
        }
      }

      const stateObject = { recaptchaToken };
      const stateValue = btoa(JSON.stringify(stateObject));
      redirectAuth0(tenant.auth0Domain, tenant.auth0ClientId, redirectUri, email, stateValue);
    },
    [email, redirecting, isEmailRequired]
  );

  const loginWithRedirect = useCallback(async (recaptchaToken) => {
    if (!loginRedirectProcessed) {
      setLoginRedirectProcessed(true);
    }
    else {
      return;
    }

    const masterTenant = {
      name: "Master",
      auth0Domain: authConfig.domain,
      auth0ClientId: authConfig.clientId,
      apiBaseUrl: authConfig.redirectUri,
    };

    if (isEmailRequired) {
      const handled = await handleTenantFetchByEmail({
        email, validateEmail, dispatch, setEmailError, setButtonAuth0Text, setLoginRedirectProcessed,
        setTenants, setShowTenantSelection, setSelectedTenant, handleTenantRedirect
      });

      if (handled) return;
    }

    handleTenantRedirect(masterTenant, recaptchaToken);
  }, [loginRedirectProcessed,
    setLoginRedirectProcessed,
    setButtonAuth0Text,
    handleTenantRedirect,
    isEmailRequired,
    email,
    dispatch,]);

  useEffect(() => {
    const fetchAuthConfig = async () => {
      try {
        const { data } = await axios.get("/api/config/auth");
        setEnableRecaptcha(data.enableRecaptcha);
        if (data.recaptchaSiteKey) {
          setRecaptchaSiteKey(data.recaptchaSiteKey);
        }
      } catch (error) {
        logErrorToSentry(error, {
          service: "login",
          event: "fetch_auth_config_failed",
        });
        setEnableRecaptcha(false);
      }
    };

    const envSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (envSiteKey) {
      setRecaptchaSiteKey(envSiteKey);
    }

    fetchAuthConfig();

    const saved = localStorage.getItem("lastLoginEmail");
    if (saved) setEmail(saved);

    // TODO fix duplicate calls
    handleAuthToken(dispatch, setJwt, navigate, triggerDashboardTutorial).finally(async () => {
      setIsLoading(false);
    });
  }, [dispatch, setJwt, isAuth0Enabled, navigate, triggerDashboardTutorial]);

  const formik = useFormik({
    initialValues: {
      username: "",
      password: "",
    },
    validationSchema: LoginSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setSubmitting(true);

      const loginData = {
        ...values,
        ...(captchaToken && { captchaToken })
      };

      await authLogin(loginData)
        .then(async (res) => {
          setSubmitting(false);
          if (res) {
            const { token, user = null } = res;
            axios.defaults.headers.common["x-auth"] = token;
            sessionStorage.setItem("x-auth", token);
            await dispatch({ type: LOGIN, payload: user });
            await dispatch(setRoleName(user?.role?.slug));
            // await getAccount(user, isMobile);
            await setJwt(token);
            dispatch({ type: SET_ACCOUNTS, payload: "refreshing" });
            if (!user?.requirePasswordReset) {
              setTimeout(() => triggerDashboardTutorial(), 5000);
            }
            setRequiresCaptcha(false);
            setCaptchaToken(null);
            if (recaptchaRef.current) {
              recaptchaRef.current.reset();
            }
            resetForm();
          }
        })
        .catch((err) => {
          setSubmitting(false);

          if (err?.response?.data?.requiresCaptcha) {
            setRequiresCaptcha(true);
            setCaptchaToken(null);
            if (recaptchaRef.current) {
              recaptchaRef.current.reset();
            }
          }
        });
    },
  });

  const { errors, touched, isSubmitting, getFieldProps } = formik;

  if (isLoading || isLoginLoading) { return (<></>) }

  return (
    <>
      <Container maxWidth="sm">
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: "80vh" }}
        >
          <Grid sx={{ padding: 5 }}>
            <TempLogo />
          </Grid>
          <Grid>
            <Typography variant="h5">Welcome to SNF Data</Typography>
          </Grid>
          <Grid sx={{ mt: 4 }} width="80%">
            {isAuth0Enabled ? (
              <Stack spacing={2} maxWidth="sm">

                <FormControl>
                  <FormLabel>Email</FormLabel>
                  <TextField
                    fullWidth
                    name="email"
                    id="email"
                    autoComplete="email"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const emailValue = e.target.value;
                      setEmail(emailValue);
                      setEmailError('');
                    }}
                    error={Boolean(emailError)}
                    helperText={emailError}
                    disabled={showRecaptcha}
                  />
                </FormControl>

                <LoadingButton
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setButtonAuth0Text("Logging you in ...");
                    if (enableRecaptcha) {
                      setShowRecaptcha(true);
                    } else {
                      if (!showTenantSelection) {
                        loginWithRedirect();
                      } else {
                        handleTenantRedirect(selectedTenant);
                      }
                    }
                  }}
                  disabled={showRecaptcha || (showTenantSelection && !selectedTenant)}
                >
                  {buttonAuth0Text}
                </LoadingButton>
                {enableRecaptcha && showRecaptcha && recaptchaSiteKey && (
                  <div style={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                    <div
                      style={{
                        transform: "scale(0.72)",
                        transformOrigin: "top right",
                      }}
                    >
                      <ReCAPTCHA
                        sitekey={recaptchaSiteKey}
                        onChange={onRecaptchaResolved}
                        onExpired={resetRecaptcha}
                        onError={resetRecaptcha}
                      />
                    </div>
                  </div>
                )}
                {showTenantSelection && (
                  <FormControl fullWidth>
                    <FormLabel>Select Organization</FormLabel>
                    <Select
                      value={selectedTenant ? selectedTenant.id || "master" : ""}
                      onChange={(e) => {
                        const chosen = tenants.find((t) => t.id === e.target.value) || tenants.find((t) => !t.id);
                        setSelectedTenant(chosen || null);
                      }}
                    >
                      {tenants.map((tenant) => (
                        <MenuItem value={tenant.id || "master"} key={tenant.id || "master"}>
                          {tenant.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Stack>
            ) : (
              <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                  <Stack spacing={2} maxWidth="sm">
                    <Stack spacing={3}>
                      <FormControl>
                        <FormLabel>Username</FormLabel>
                        <TextField
                          {...getFieldProps("username")}
                          fullWidth
                          autoComplete="off"
                          type={"text"}
                          error={Boolean(touched.username && errors.username)}
                          helperText={touched.username && errors.username}
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Password</FormLabel>
                        <TextField
                          {...getFieldProps("password")}
                          fullWidth
                          autoComplete="off"
                          type={showPassword ? "text" : "password"}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={handleShowPassword}
                                  edge="end"
                                  size="small"
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          error={Boolean(touched.password && errors.password)}
                          helperText={touched.password && errors.password}
                        />
                      </FormControl>

                      {requiresCaptcha && recaptchaSiteKey && (
                        <FormControl>
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={recaptchaSiteKey}
                            onChange={(token) => setCaptchaToken(token)}
                            onExpired={() => setCaptchaToken(null)}
                            onError={() => setCaptchaToken(null)}
                          />
                        </FormControl>
                      )}
                    </Stack>
                    <Stack>
                      <LoadingButton
                        type="submit"
                        variant="contained"
                        loading={isSubmitting}
                        loadingIndicator="Loading..."
                        size="large"
                        disabled={requiresCaptcha && !captchaToken}
                      >
                        Login
                      </LoadingButton>
                    </Stack>
                  </Stack>
                </Form>
              </FormikProvider>
            )}
          </Grid>
          {!isAuth0Enabled && (
            <Grid sx={{ mt: 2 }}>
              <Link
                to="/forgot-password"
                className={`${styles.selectionOption} ${styles.forUser}`}
              >
                Forgot Password ?
              </Link>
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

export default Login;
